
import { useState, useRef } from "react";
import { TikTokVideo } from "@/types/api";
import { ProgressState } from "@/hooks/useBulkDownload";

const MAX_RETRIES = 3;

export function useVideoDownloader() {
  const [progressState, setProgressState] = useState<ProgressState>({});
  const downloadControllers = useRef<{ [id: string]: AbortController }>({});

  const downloadSingle = async (
    video: TikTokVideo, 
    retry: number, 
    idxInQueue: number, 
    onComplete: (nextIdx: number) => void,
    cancelAll: boolean,
    pausedVideos: string[]
  ): Promise<void> => {
    setProgressState((state) => ({
      ...state,
      [video.id]: {
        ...state[video.id],
        status: "in_progress",
        retries: retry,
        startTime: Date.now(),
      }
    }));

    const controller = new AbortController();
    downloadControllers.current[video.id] = controller;

    try {
      const res = await fetch(video.video_url, { signal: controller.signal });
      if (!res.ok) {
        throw new Error(res.statusText || "Failed to download video URL");
      }
      
      const blob = await res.blob();
      setProgressState((state) => ({
        ...state,
        [video.id]: {
          ...state[video.id],
          status: "complete",
          blob,
          endTime: Date.now(),
          retries: retry
        }
      }));
      onComplete(idxInQueue + 1);
    } catch (err: any) {
      if (retry < MAX_RETRIES && !cancelAll && !pausedVideos.includes(video.id)) {
        setTimeout(() => downloadSingle(video, retry + 1, idxInQueue, onComplete, cancelAll, pausedVideos), 600 * (retry + 1));
      } else {
        setProgressState((state) => ({
          ...state,
          [video.id]: {
            ...(state[video.id] || {}),
            status: "failed",
            error: err?.message || "Download failed",
            endTime: Date.now(),
            retries: retry
          }
        }));
        onComplete(idxInQueue + 1);
      }
    }
  };

  return {
    progressState,
    setProgressState,
    downloadControllers,
    downloadSingle
  };
}
